import { ResponseParams, requestHttpGet, requestHttpPost, requestHttpPut, ApiError } from './RequestBase';

/**
 * グループ一覧取得
 * 学研IDに紐づくグループとグループ内の会員情報を取得する。パラメータとして区分を渡せるようにして、画面からは「家族」で絞り込んで使う。
 */
type GetGroupReqData = {
	group_type: number;
};
type GetGroupResData = {
	group_list: Group[];
};
export type Group = {
	group_id: string;
	group_type: number;
	group_name: string;
	regist_ts: string;
	update_ts: string;
	gid_list: UserWithGroupAuth[];
};
export type UserWithGroupAuth = {
	gidpk: string;
	gid: string;
	mailaddress_1: string;
	lastname: string;
	firstname: string;
	kana_lastname: string;
	kana_firstname: string;
	birth: string;
	sex: number;
	payment_not_flg: number;
	member_flg: number;
	group_auth: number;
	member_updatable_flg: number;
	regist_ts: string;
	update_ts: string;
};
export const callGetGroupApi = async (groupType: GroupTypes): Promise<ResponseParams<GetGroupResData>> => {
	const getGroupReqData: GetGroupReqData = { group_type: groupType.value };
	const result: ResponseParams<GetGroupResData> = await requestHttpGet(`/groups`, getGroupReqData);
	return result;
};

/**
 * グループ登録
 */
type RegisterGroupReqData = {
	group_type: number;
	group_name?: string;
};
type RegisterGroupResData = {
	group_id: string;
};
export const callRegisterGroupApi = async (
	groupType: GroupTypes,
	groupName?: string,
): Promise<ResponseParams<RegisterGroupResData>> => {
	const body: RegisterGroupReqData = { group_type: groupType.value, group_name: groupName };
	const result: ResponseParams<RegisterGroupResData> = await requestHttpPost(`/groups`, body);
	return result;
};

/**
 * グループメンバー登録
 * メンバーを追加して、自身のグループに参加させる。グループが無い場合は新規作成する。
 */
export type RegisterGroupMemberReqData = {
	gid: string;
	pass: string;
	lastname: string;
	firstname: string;
	kana_lastname?: string;
	kana_firstname?: string;
	birth: string;
	sex?: number;
	agree_flg?: number;
	data_util_agree_flg?: number;
};
export const callRegisterGroupMemberApi = async (
	groupId: string,
	body: RegisterGroupMemberReqData,
): Promise<ResponseParams<void>> => {
	const result: ResponseParams<void> = await requestHttpPost(`/groups/${groupId}/member`, body);
	return result;
};

/**
 * グループメンバー更新
 * 自身の所属するグループ内のメンバーフラグが立っている会員データのみ更新が可能。削除は退会にするだけ。
 */
export type UpdateGroupMemberReqData = {
	gidpk: string;
	gid: string;
	pass?: string;
	lastname: string;
	firstname: string;
	kana_lastname?: string;
	kana_firstname?: string;
	birth: string;
	sex?: number;
};
export const callUpdateGroupMemberApi = async (
	groupId: string,
	body: UpdateGroupMemberReqData,
): Promise<ResponseParams<void>> => {
	const result: ResponseParams<void> = await requestHttpPut(`/groups/${groupId}/member`, body);
	return result;
};

/**
 * グループメンバー削除
 */
export const callDeleteGroupMemberApi = async (
	groupId: string,
	targetGidpk: string,
): Promise<ResponseParams<void | ApiError>> => {
	const result: ResponseParams<void | ApiError> = await requestHttpPost(`/groups/${groupId}/member/delete`, {
		gidpk: targetGidpk,
	});
	return result;
};

/**
 * グループからの離脱
 * 自身と場合によってはメンバーを連れて、グループから離脱する。メンバーを連れていく場合は、新規グループも合わせて作成する。
 */
type GidpkObj = {
	gidpk: string;
};
export type LeaveFamilyReqData = {
	gidpk_list?: GidpkObj[];
	group_name: string;
};
export const callLeaveFamilyApi = async (
	groupId: string,
	body: LeaveFamilyReqData,
): Promise<ResponseParams<void | ApiError>> => {
	const result: ResponseParams<void | ApiError> = await requestHttpPost(`/groups/${groupId}/user/delete`, body);
	return result;
};

/**
 * グループ会員の除外
 * グループから該当の会員を除外する。
 */
export const callGetOutFamilyApi = async (
	groupId: string,
	targetGidpk: string,
): Promise<ResponseParams<void | ApiError>> => {
	const result: ResponseParams<void | ApiError> = await requestHttpPost(`/groups/${groupId}/general/delete`, {
		gidpk: targetGidpk,
	});
	return result;
};

/**
 * グループ一般会員招待
 */
type SendInvitationReqData = {
	mailaddress: string;
};
export const callSendInvitationApi = async (
	groupId: string,
	body: SendInvitationReqData,
): Promise<ResponseParams<void | ApiError>> => {
	const result: ResponseParams<void | ApiError> = await requestHttpPost(`/groups/${groupId}/general`, body);
	return result;
};

/**
 * 招待取得
 */
export type GetInvitationResData = {
	invite_id: string;
	lastname: string;
	firstname: string;
};
export const callGetInvitationApi = async (inviteId: string): Promise<ResponseParams<GetInvitationResData>> => {
	const result: ResponseParams<GetInvitationResData> = await requestHttpGet(`/invitations/${inviteId}`);
	return result;
};

/**
 * 招待承認
 */
type ApproveInvitationReqData = { invite_id: string };
export const callApproveInvitationApi = async (body: ApproveInvitationReqData): Promise<ResponseParams<void>> => {
	const result: ResponseParams<void> = await requestHttpPost(`/invitations`, body);
	return result;
};

/**
 * グループ一般会員更新
 * 決済不可フラグ、メンバー更新可能フラグを更新できる。
 */
export type UpdateGroupUserReqData = {
	gidpk: string;
	payment_not_flg: number;
	member_updatable_flg: number;
};
export const callUpdateGroupUserApi = async (
	groupId: string,
	body: UpdateGroupUserReqData,
): Promise<ResponseParams<void>> => {
	const result: ResponseParams<void> = await requestHttpPut(`/groups/${groupId}/general`, body);
	return result;
};

/**
 * グループ代表者変更
 * 代表者を変更できる。
 */
type ChangeGroupAdminReqData = {
	gidpk: string;
};
export const callChangeGroupAdminApi = async (
	groupId: string,
	body: ChangeGroupAdminReqData,
): Promise<ResponseParams<void | ApiError>> => {
	const result: ResponseParams<void | ApiError> = await requestHttpPost(`/groups/${groupId}/representative`, body);
	return result;
};

// その他type
export type GroupWithLoginUser = Group & {
	loginUser: UserWithGroupAuth;
};

export const GroupType = {
	FAMILY: { value: 1 },
} as const;
type GroupTypes = typeof GroupType[keyof typeof GroupType];
